import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useCatalog } from "../catalogStore";
import { ProductCard, CATEGORY_CSS } from "./CategoryShared";

/**
 * Products of ONE category — route:  /category/:slug
 * Reads the shared storefront copy (see catalogStore.js), so when it has
 * already been loaded the page appears instantly with no request at all.
 *
 * Optional props (only if your app has a wishlist):
 *   wishlist           array of products / ids already wishlisted
 *   onToggleWishlist   (product) => void
 */
export default function CategoryProducts({ wishlist = [], onToggleWishlist }) {
  const { slug } = useParams();
  const { data, fresh, error } = useCatalog();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const key = String(slug || "").toLowerCase();

  const category = useMemo(
    () => (data ? data.categories.find(
      (c) => c.slug === key || String(c.name).toLowerCase() === key
    ) : null),
    [data, key]
  );

  const products = useMemo(
    () => (category
      ? data.products.filter((p) => String(p.categoryId) === String(category._id))
      : []),
    [data, category]
  );

  // loading | ready | notfound | error
  let status = "loading";
  if (category)                status = "ready";
  else if (data && fresh)      status = "notfound";   // only once the copy is confirmed by the server
  else if (!data && error)     status = "error";

  return (
    <div className="cp-wrap">
      <style>{CATEGORY_CSS}</style>

      {status === "loading" && <div className="cp-msg">Loading products…</div>}

      {status === "notfound" && (
        <div className="cp-msg">
          This category was not found.
          <br />
          <Link to="/home" className="cp-btn">BACK TO HOME</Link>
        </div>
      )}

      {status === "error" && (
        <div className="cp-msg">
          Could not load products right now. Please try again.
          <br />
          <Link to="/home" className="cp-btn">BACK TO HOME</Link>
        </div>
      )}

      {status === "ready" && (
        <>
          <div className="cp-head">
            <Link to="/category" className="cp-back">← All categories</Link>
            <h1 className="cp-title">{category.name}</h1>
            <span className="cp-count">
              {products.length} {products.length === 1 ? "Product" : "Products"}
            </span>
          </div>

          {products.length === 0 ? (
            <div className="cp-msg">
              No products available in this category
              <br />
              <Link to="/home" className="cp-btn">CONTINUE SHOPPING</Link>
            </div>
          ) : (
            <div className="cp-grid">
              {products.map((p) => (
                <ProductCard key={p._id} p={p} wishlist={wishlist} onToggleWishlist={onToggleWishlist} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}